/**
 * Milestones Database Operations
 *
 * Provides CRUD operations for project milestones that represent
 * significant checkpoints or deliverables in a project.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError, buildUpdatePayload } from './helpers';
import { isCompletedStatus } from '@/constants/taskStatuses';

// ============================================
// TYPES
// ============================================

export type MilestoneStatus = 'open' | 'in_progress' | 'completed' | 'missed';

export interface Milestone {
  id: string;
  spaceId: string;
  name: string;
  description: string | null;
  targetDate: number | null;
  status: MilestoneStatus;
  color: string;
  orderIndex: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MilestoneWithProgress extends Milestone {
  totalTasks: number;
  completedTasks: number;
  progressPercent: number;
}

export interface CreateMilestoneInput {
  spaceId: string;
  name: string;
  description?: string;
  targetDate?: number;
  status?: MilestoneStatus;
  color?: string;
  createdBy?: string;
}

export interface UpdateMilestoneInput {
  name?: string;
  description?: string | null;
  targetDate?: number | null;
  status?: MilestoneStatus;
  color?: string;
  orderIndex?: number;
}

// ============================================
// CONVERSION HELPERS
// ============================================

function convertMilestoneFromSupabase(row: Record<string, unknown>): Milestone {
  return {
    id: row.id as string,
    spaceId: row.space_id as string,
    name: row.name as string,
    description: (row.description as string) || null,
    targetDate: (row.target_date as number) || null,
    status: (row.status as MilestoneStatus) || 'open',
    color: (row.color as string) || '#6366f1',
    orderIndex: (row.order_index as number) || 0,
    createdBy: (row.created_by as string) || null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

// ============================================
// PROGRESS CALCULATION HELPER
// ============================================

function _calculateMilestoneProgress(
  milestones: Milestone[],
  tasks: Array<{ milestone_id: string; status: string }> | null
): MilestoneWithProgress[] {
  const milestoneIds = milestones.map((m) => m.id);
  const progressMap = new Map<string, { total: number; completed: number }>();
  milestoneIds.forEach((id) => progressMap.set(id, { total: 0, completed: 0 }));

  (tasks || []).forEach((task) => {
    const progress = progressMap.get(task.milestone_id);
    if (progress) {
      progress.total++;
      if (isCompletedStatus(task.status)) {
        progress.completed++;
      }
    }
  });

  return milestones.map((milestone) => {
    const progress = progressMap.get(milestone.id) || { total: 0, completed: 0 };
    return {
      ...milestone,
      totalTasks: progress.total,
      completedTasks: progress.completed,
      progressPercent: progress.total > 0
        ? Math.round((progress.completed / progress.total) * 100)
        : 0,
    };
  });
}

// ============================================
// MILESTONE CRUD OPERATIONS
// ============================================

/**
 * Create a new milestone
 */
export async function createMilestone(input: CreateMilestoneInput): Promise<Milestone> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('milestones')
      .insert({
        space_id: input.spaceId,
        name: input.name,
        description: input.description || null,
        target_date: input.targetDate || null,
        status: input.status || 'open',
        color: input.color || '#6366f1',
        created_by: input.createdBy || null,
      })
      .select()
      .single()
  );

  if (error) throw error;
  return convertMilestoneFromSupabase(data);
}

/**
 * Get a milestone by ID
 */
export async function getMilestone(milestoneId: string): Promise<Milestone | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('milestones')
      .select('*')
      .eq('id', milestoneId)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertMilestoneFromSupabase(data);
}

/**
 * Get all milestones for a space
 */
export async function getMilestonesBySpace(spaceId: string): Promise<Milestone[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('milestones')
      .select('*')
      .eq('space_id', spaceId)
      .order('target_date', { ascending: true, nullsFirst: false })
      .order('order_index', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertMilestoneFromSupabase);
}

/**
 * Get milestones with task progress
 */
export async function getMilestonesWithProgress(spaceId: string): Promise<MilestoneWithProgress[]> {
  // Get milestones
  const milestones = await getMilestonesBySpace(spaceId);

  if (milestones.length === 0) return [];

  const milestoneIds = milestones.map((m) => m.id);
  const { data: tasks, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('milestone_id, status')
      .in('milestone_id', milestoneIds)
  );

  if (error) throw error;
  return _calculateMilestoneProgress(milestones, tasks);
}

/**
 * Get upcoming milestones (next N days)
 */
export async function getUpcomingMilestones(options: {
  spaceId?: string;
  days?: number;
  limit?: number;
}): Promise<MilestoneWithProgress[]> {
  const { spaceId, days = 30, limit = 10 } = options;

  const now = Date.now();
  const futureDate = now + days * 24 * 60 * 60 * 1000;

  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('milestones')
      .select('*')
      .gte('target_date', now)
      .lte('target_date', futureDate)
      .in('status', ['open', 'in_progress'])
      .order('target_date', { ascending: true })
      .limit(limit);

    if (spaceId) {
      query = query.eq('space_id', spaceId);
    }

    return query;
  });

  if (error) throw error;

  const milestones = (data || []).map(convertMilestoneFromSupabase);

  if (milestones.length === 0) return [];

  const milestoneIds = milestones.map((m) => m.id);
  const { data: tasks } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('milestone_id, status')
      .in('milestone_id', milestoneIds)
  );

  return _calculateMilestoneProgress(milestones, tasks);
}

/**
 * Get overdue milestones
 */
export async function getOverdueMilestones(spaceId?: string): Promise<MilestoneWithProgress[]> {
  const now = Date.now();

  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('milestones')
      .select('*')
      .lt('target_date', now)
      .in('status', ['open', 'in_progress'])
      .order('target_date', { ascending: true });

    if (spaceId) {
      query = query.eq('space_id', spaceId);
    }

    return query;
  });

  if (error) throw error;

  const milestones = (data || []).map(convertMilestoneFromSupabase);

  if (milestones.length === 0) return [];

  const milestoneIds = milestones.map((m) => m.id);
  const { data: tasks } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('milestone_id, status')
      .in('milestone_id', milestoneIds)
  );

  return _calculateMilestoneProgress(milestones, tasks);
}

/**
 * Update a milestone
 */
export async function updateMilestone(
  milestoneId: string,
  updates: UpdateMilestoneInput
): Promise<Milestone> {
  const updateData = buildUpdatePayload(updates as Record<string, unknown>, {
    name: 'name',
    description: 'description',
    targetDate: 'target_date',
    status: 'status',
    color: 'color',
    orderIndex: 'order_index',
  });

  const { data, error } = await withRetry(() =>
    supabase
      .from('milestones')
      .update(updateData)
      .eq('id', milestoneId)
      .select()
      .single()
  );

  if (error) throw error;
  return convertMilestoneFromSupabase(data);
}

/**
 * Delete a milestone
 */
export async function deleteMilestone(milestoneId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('milestones')
      .delete()
      .eq('id', milestoneId)
  );

  if (error) throw error;
}

/**
 * Complete a milestone
 */
export async function completeMilestone(milestoneId: string): Promise<Milestone> {
  return updateMilestone(milestoneId, { status: 'completed' });
}

/**
 * Mark milestone as missed
 */
export async function markMilestoneMissed(milestoneId: string): Promise<Milestone> {
  return updateMilestone(milestoneId, { status: 'missed' });
}

/**
 * Reorder milestones
 */
export async function reorderMilestones(
  milestoneIds: string[]
): Promise<void> {
  const updates = milestoneIds.map((id, index) => ({
    id,
    order_index: index,
  }));

  for (const update of updates) {
    const { error } = await withRetry(() =>
      supabase
        .from('milestones')
        .update({ order_index: update.order_index })
        .eq('id', update.id)
    );

    if (error) throw error;
  }
}

// ============================================
// TASK-MILESTONE OPERATIONS
// ============================================

/**
 * Assign a task to a milestone
 */
export async function assignTaskToMilestone(
  taskId: string,
  milestoneId: string | null
): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ milestone_id: milestoneId })
      .eq('id', taskId)
  );

  if (error) throw error;
}

/**
 * Get all tasks for a milestone
 */
export async function getTasksByMilestone(milestoneId: string): Promise<Array<{
  id: string;
  name: string;
  status: string;
  dueDate: number | null;
  assignees: string[];
}>> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('id, name, status, due_date, assignees')
      .eq('milestone_id', milestoneId)
      .order('due_date', { ascending: true, nullsFirst: false })
  );

  if (error) throw error;

  return (data || []).map((task) => ({
    id: task.id,
    name: task.name,
    status: task.status,
    dueDate: task.due_date,
    assignees: task.assignees || [],
  }));
}

/**
 * Bulk assign tasks to a milestone
 */
export async function bulkAssignTasksToMilestone(
  taskIds: string[],
  milestoneId: string | null
): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ milestone_id: milestoneId })
      .in('id', taskIds)
  );

  if (error) throw error;
}

// ============================================
// MILESTONE STATISTICS
// ============================================

/**
 * Get milestone statistics for a space
 */
export async function getMilestoneStats(spaceId: string): Promise<{
  total: number;
  open: number;
  inProgress: number;
  completed: number;
  missed: number;
  overdue: number;
  upcomingThisWeek: number;
}> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('milestones')
      .select('status, target_date')
      .eq('space_id', spaceId)
  );

  if (error) throw error;

  const now = Date.now();
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;

  const stats = {
    total: 0,
    open: 0,
    inProgress: 0,
    completed: 0,
    missed: 0,
    overdue: 0,
    upcomingThisWeek: 0,
  };

  (data || []).forEach((milestone) => {
    stats.total++;

    switch (milestone.status) {
      case 'open':
        stats.open++;
        break;
      case 'in_progress':
        stats.inProgress++;
        break;
      case 'completed':
        stats.completed++;
        break;
      case 'missed':
        stats.missed++;
        break;
    }

    // Check overdue (not completed and past target date)
    if (
      milestone.target_date &&
      milestone.target_date < now &&
      milestone.status !== 'completed' &&
      milestone.status !== 'missed'
    ) {
      stats.overdue++;
    }

    // Check upcoming this week
    if (
      milestone.target_date &&
      milestone.target_date >= now &&
      milestone.target_date <= weekFromNow &&
      milestone.status !== 'completed' &&
      milestone.status !== 'missed'
    ) {
      stats.upcomingThisWeek++;
    }
  });

  return stats;
}

// ============================================
// CONSTANTS
// ============================================

export const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  open: 'Aperto',
  in_progress: 'In Corso',
  completed: 'Completato',
  missed: 'Mancato',
};

export const MILESTONE_STATUS_COLORS: Record<MilestoneStatus, string> = {
  open: '#6b7280', // gray
  in_progress: '#3b82f6', // blue
  completed: '#22c55e', // green
  missed: '#ef4444', // red
};

export const DEFAULT_MILESTONE_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f43f5e', // rose
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
];
